import { icons } from '../icons';
import { useEffect, useRef, useState } from 'react';
import cn from '../utils/cn';
import KoobNavbarUser from './KoobNavbarUser';
import KoobNavbarAvatar from './KoobNavbarAvatar';

function MenuItem({ icon, label, onClick, destructive = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 w-full px-3 py-2 rounded-lg border-none bg-transparent cursor-pointer text-left text-sm transition',
        destructive
          ? 'text-red-600 hover:bg-red-50'
          : 'text-k-h hover:bg-gray-100',
      )}
    >
      <i
        className={cn(
          icon,
          'fa-fw text-base',
          destructive ? 'text-red-600' : 'text-k-p',
        )}
      />
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * Presentational user dropdown for the navbar. Renders the user block as the
 * trigger and a menu panel (profile / organization / support / logout). Open
 * state (toggle on click, close on outside click + Escape) is managed
 * internally. Every action is a prop callback — the app wires the real
 * routes/logout in US-006.
 */
export default function KoobNavbarUserDropdown({
  name,
  organization,
  email,
  role,
  // Optional image hook for the future — no image lookup is done yet.
  imageUrl,
  // When true: a red pill with Stop beside the trigger, a red ring on the
  // avatar and a banner in the panel, so an admin never forgets.
  impersonating = false,
  // Labels (kept as props so the app passes already-translated strings).
  profileLabel = 'My Profile',
  organizationLabel = 'My Organization',
  travelDiaryLabel = 'Travel Diary Settings',
  travelDiaryContentLabel = 'Travel Diary Content',
  travelDiaryThemeLabel = 'Travel Diary Theme',
  auditLogLabel = 'Audit log',
  supportLabel = 'Support Center',
  logoutLabel = 'Log out',
  impersonatingLabel = 'Impersonating',
  impersonatingBannerLabel = 'Impersonating · acting as this user',
  stopImpersonatingLabel = 'Stop',
  onProfile,
  onOrganization,
  onTravelDiary,
  onTravelDiaryContent,
  onTravelDiaryTheme,
  onAuditLog,
  onSupport,
  onLogout,
  onStopImpersonating,
  className,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = () => setOpen(false);
  const toggle = () => setOpen((current) => !current);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        close();
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Wrap an action callback so the menu closes after it runs.
  const handleAction = (callback) => () => {
    close();
    callback?.();
  };

  return (
    <div ref={containerRef} className={cn('relative', className)} {...props}>
      <div className="flex items-center gap-3">
        {/* Impersonation: a pill beside the user, so the button keeps its
            size and the way out is one click away. */}
        {impersonating && (
          <span className="hidden sm:inline-flex items-center gap-2 rounded-full bg-red-50 py-1 pl-3 pr-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            {impersonatingLabel}
            {onStopImpersonating && (
              <button
                type="button"
                onClick={onStopImpersonating}
                className="inline-flex items-center gap-1 rounded-full border-none bg-white px-2 py-0.5 text-xs font-semibold text-red-700 cursor-pointer hover:bg-red-100"
              >
                <i className={`${icons.impersonationStop} text-[10px]`} />
                {stopImpersonatingLabel}
              </button>
            )}
          </span>
        )}
        <KoobNavbarUser
          name={name}
          organization={organization}
          imageUrl={imageUrl}
          impersonating={impersonating}
          onClick={toggle}
        />
      </div>

      {open && (
        <div className="absolute right-0 mt-2 w-72 z-popover rounded-xl border border-gray-100 bg-white shadow-lg overflow-hidden">
          {/* Impersonation banner */}
          {impersonating && (
            <div className="flex items-center justify-between px-4 py-2 bg-red-50 border-b border-red-100">
              <span className="flex items-center gap-2 text-[11px] font-semibold text-red-700">
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                {impersonatingBannerLabel}
              </span>
              <button
                type="button"
                onClick={handleAction(onStopImpersonating)}
                className="flex items-center gap-1 border-none bg-transparent p-0 text-[11px] font-semibold text-red-700 hover:underline cursor-pointer"
              >
                <i className={`${icons.impersonationStop} text-xs`} />
                {stopImpersonatingLabel}
              </button>
            </div>
          )}

          {/* Header */}
          <div className="px-4 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3">
              <KoobNavbarAvatar
                name={name}
                imageUrl={imageUrl}
                impersonating={impersonating}
                size="lg"
              />
              <span className="min-w-0 flex flex-col">
                <span className="text-sm font-bold text-k-h truncate">
                  {name}
                </span>
                {email && (
                  <span className="text-xs text-k-p truncate">{email}</span>
                )}
              </span>
            </div>

            {(organization || role) && (
              <div className="mt-3 flex items-center justify-between gap-2">
                {organization && (
                  <span className="text-sm text-k-p truncate">
                    {organization}
                  </span>
                )}
                {role && (
                  <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-600">
                    {role}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-2">
            <MenuItem
              icon={icons.profile}
              label={profileLabel}
              onClick={handleAction(onProfile)}
            />
            {onOrganization && (
              <MenuItem
                icon={icons.agency}
                label={organizationLabel}
                onClick={handleAction(onOrganization)}
              />
            )}
            {onTravelDiaryContent && (
              <MenuItem
                icon={icons.travelDiaryContent}
                label={travelDiaryContentLabel}
                onClick={handleAction(onTravelDiaryContent)}
              />
            )}
            {onTravelDiaryTheme && (
              <MenuItem
                icon={icons.theme}
                label={travelDiaryThemeLabel}
                onClick={handleAction(onTravelDiaryTheme)}
              />
            )}
            {!onTravelDiaryContent && !onTravelDiaryTheme && onTravelDiary && (
              <MenuItem
                icon={icons.travelDiaryContent}
                label={travelDiaryLabel}
                onClick={handleAction(onTravelDiary)}
                destructive={false}
              />
            )}
            {onAuditLog && (
              <MenuItem
                icon={icons.history}
                label={auditLogLabel}
                onClick={handleAction(onAuditLog)}
              />
            )}
            <MenuItem
              icon={icons.help}
              label={supportLabel}
              onClick={handleAction(onSupport)}
            />
          </div>

          {/* Separator + destructive actions */}
          <div className="border-t border-gray-100 p-2">
            <MenuItem
              icon={icons.logout}
              label={logoutLabel}
              onClick={handleAction(onLogout)}
              destructive
            />
          </div>
        </div>
      )}
    </div>
  );
}
